import { useEffect, useState, useCallback, useRef } from "preact/hooks";

import Wordmark from "../assets/MURMURATIONS.svg";
import SplashVideo from "../assets/splash.mp4";
import SplashPoster from "../assets/splash-poster.webp";
import "../microsite/microsite.scss";

interface EnterSplashProps {
  /** Dismisses the splash (after the fade-out). Only the CTA fires this. */
  onEnter: () => void;
  /** Fires synchronously inside the CTA's click handler — for work that must
   * happen within the user gesture (iOS Safari audio playback). */
  onEnterPressed?: () => void;
}

/**
 * "Enter the Murmuration" preload gate, shown on every microsite page load
 * (full shell and ?mode=ar minimal landing alike). A centred lockup —
 * wordmark · tagline · body · CTA — over a looping, muted murmuration video
 * (Figma node 2820:963). Dismissed only by tapping the CTA; the digital twin
 * loads behind it, so dismissal lands on either the live flock or its loading
 * state.
 *
 * The layout is centred on every viewport (the design is device-agnostic);
 * type and spacing scale at the $narrow breakpoint. Styling: .es-* in
 * microsite.scss.
 */
export function EnterSplash({ onEnter, onEnterPressed }: EnterSplashProps) {
  // Brief fade-out on dismiss instead of a hard cut: the CTA sets `closing`,
  // the gate fades over 450ms, then the parent unmounts it.
  const [closing, setClosing] = useState(false);
  // Reduced-motion users get the static poster still instead of the loop —
  // same convention as IntroSequence / MicrositeShell's background videos.
  const [reduceMotion] = useState(() => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  // The video fades in once it actually starts playing — iOS can take a couple
  // of seconds to buffer + decode the 1080p clip, and snapping it in on top of
  // the poster looks rough. Until `playing`, the poster still carries the image.
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleEnter = useCallback(() => {
    if (closing) return;
    onEnterPressed?.();
    setClosing(true);
    window.setTimeout(onEnter, 450);
  }, [closing, onEnter, onEnterPressed]);

  // Hold the page still while the gate is up. Deterministic set/clear (no
  // save/restore — see the matching comment in MicrositeShell's sheet lock).
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Some mobile browsers honour `autoplay` only after an explicit play() call;
  // nudge it on mount (muted + playsInline keeps this gesture-free). A blocked
  // play() just leaves the poster underlay up, which is the intended fallback.
  useEffect(() => {
    if (reduceMotion) return;
    // `?.catch` not `.catch`: a blocked/unsupported play() can return undefined
    // (older browsers, jsdom) rather than a Promise — same guard as MicrositeShell.
    videoRef.current?.play()?.catch(() => {});
  }, [reduceMotion]);

  // Release the video's hardware decoder + buffer — but only AFTER the gate has
  // finished fading out. Tearing it down at fade-start snapped the clip away
  // mid-fade; instead it keeps playing through the 450ms fade and we release
  // just after, on a captured element ref so it still fires once Preact has
  // unmounted us. pause() alone keeps the decoder resident, so drop the src +
  // load() too (the teardown StoryPlayer / useDecodeGatedVideo use). The poster
  // underlay carries the image throughout, so the release is never visible.
  // Intentionally not cleared on unmount — the parent unmounts us at ~450ms and
  // a cleanup would cancel the release before it runs.
  useEffect(() => {
    if (!closing) return;
    const video = videoRef.current;
    if (!video) return;
    window.setTimeout(() => {
      video.pause();
      video.removeAttribute("src");
      video.load();
    }, 600);
  }, [closing]);

  return (
    <div className={`es-splash${closing ? " is-closing" : ""}`} role="dialog" aria-modal="true" aria-label="Welcome to Murmurations">
      {/* Still underlay — a frame of the clip itself, so it paints instantly
          and the video crossfades over matching imagery (no flash to a
          different image). Carries the backdrop on its own for reduced-motion
          and during iOS's buffering delay. */}
      <img className="es-splash__poster" src={SplashPoster} alt="" aria-hidden="true" />

      {/* Looping, muted murmuration backdrop (Figma node 2820:963). Fades in on
          `playing` so the clip never snaps in after iOS's buffer/decode delay;
          src stays put through the gate's fade-out and the deferred teardown
          frees the decoder once we've unmounted. */}
      {!reduceMotion && (
        <video
          ref={videoRef}
          className={`es-splash__video${videoReady ? " is-ready" : ""}`}
          src={SplashVideo}
          autoplay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          onPlaying={() => setVideoReady(true)}
        />
      )}

      {/* Bottom-grounding lavender scrim (Figma node 2820:970) — keeps the body
          copy and CTA legible over the brightest cloud frames. */}
      <div className="es-splash__scrim" aria-hidden="true" />

      <div className="es-splash__content">
        <img src={Wordmark} alt="Murmurations" className="es-splash__wordmark" />
        <p className="es-splash__tagline">Stories of Migration in LA</p>
        <p className="es-splash__body">
          Murmurations is a civic storytelling and public art project inspired by the collective flight of starlings, birds that move together for protection.
          In this work, each &ldquo;bird&rdquo; carries the story of a person&rsquo;s response to ICE raids in Los Angeles, reflecting a wide range of lived
          experiences across the community.
        </p>
        <button type="button" className="btn-ghost es-splash__cta" onClick={handleEnter}>
          Enter the Murmuration
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </div>
  );
}
